import { Injectable, OnDestroy } from '@angular/core';
import { map, Observable, Subscription, tap } from 'rxjs';
import { EmployeeCardFilterSetting } from '../models/employeeCardFilterSetting';
import { EmployeeSettingsService } from './employee-settings.service';
import { AuthenticationService } from './authentication.service';
import { Employee } from '../models/employee';
import { EmployeeSettingTypes } from '../../settings/enums/employeeSettingTypes';
import { ObjectTypes } from '../enums/objectTypes';
import { FilterTypes } from '../enums/filterTypes';
import { FilterViewModel } from '../models/filter/filterViewModel';
import { FilterDateOptions } from '../enums/filter/filterDateOptions';
import { AllowedFiltersService } from './allowed-filters.service';
import { RAGStatuses } from '../enums/ragStatuses';
import { ModifiableEntityViewModel } from '../../incidents/viewModels/modifiableEntityViewModel';
import { PublicIncidentReportStatuses } from '../enums/publicIncidentReportStatuses';
import { BehaviorSubject } from 'rxjs';

@Injectable()
export class SortingService implements OnDestroy {
  public filtersForSortBasedOnEnum: FilterTypes[] = [
    FilterTypes.Milestone_Type,
    FilterTypes.Risk_Likelihood,
    FilterTypes.Risk_Impact,
    FilterTypes.Risk_Action_Type,
    FilterTypes.RAG,
    FilterTypes.Risk_RAG,
    FilterTypes.Incident_Severity,
    FilterTypes.Risk_Action_Status,
    FilterTypes.Incident_Resolution_Time,
    FilterTypes.Priority,
    FilterTypes.Indicator_Priority
  ];

  private sortRAGValuesMapper = {
    [RAGStatuses.Blue]: '0',
    [RAGStatuses.Red]: '1',
    [RAGStatuses.Amber]: '2',
    [RAGStatuses.Green]: '3',
    [RAGStatuses.Grey]: '4',
  };

  private sortPIRStatusMapper = {
    [PublicIncidentReportStatuses.New]: '0',
    [PublicIncidentReportStatuses.On_Hold]: '1',
    [PublicIncidentReportStatuses.Linked]: '2',
    [PublicIncidentReportStatuses.Closed]: '3',
  }

  public MappedEnumBasedFilterTypes = {
    [FilterTypes.RAG]: this.sortRAGValuesMapper,
    [FilterTypes.Public_Incident_Report_Status]: this.sortPIRStatusMapper
  };

  public filtersToSortByFilterValue = [
    FilterTypes.Title,
    FilterTypes.Date,
    FilterTypes.RAG,
    FilterTypes.Risk_RAG,
    FilterTypes.Risk_Impact,
    FilterTypes.Risk_Likelihood,
    FilterTypes.Incident_Severity,
    FilterTypes.Public_Incident_Report_Status
  ];

  private employee: Employee;

  private cachedCardFilterSettings: EmployeeCardFilterSetting[] = [];

  private subscriptions = new Subscription();

  private _settingsChanged = new BehaviorSubject<EmployeeCardFilterSetting[]>([]);
  public settingsChanged$ = this._settingsChanged.asObservable();
  private _employeeSettingSubject = new BehaviorSubject<EmployeeCardFilterSetting[]>([]);
  public employeeSetting$ = this._employeeSettingSubject.asObservable();

  constructor(
    private readonly authenticationService: AuthenticationService,
    private readonly employeeService: EmployeeSettingsService,
    private readonly allowedFiltersService: AllowedFiltersService
  ) {
    this.subscribeForAuthentication();

    this.subscriptions.add(
      this.employeeSettingObservable.subscribe(settings => {
        this.cachedCardFilterSettings = settings;
      })
    );
  }

  /**
   * Cleanup method called when the service is destroyed
   */
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /**
   * Subscribes to authentication changes to get the current employee
   */
  subscribeForAuthentication() {
    this.subscriptions.add(
      this.authenticationService.getCurrentEmployeeObservable().subscribe((r) => {
        if (r) {
          this.employee = r;
        }
      })
    );
  }

  /**
   * Gets the employee card filter settings from the employee settings service
   * @returns Observable of EmployeeCardFilterSetting array
   */
  get employeeSettingObservable(): Observable<EmployeeCardFilterSetting[]> {
    return this.employeeService.getEmployeeSetting(EmployeeSettingTypes.Card_Filters).pipe(
      map(employeeSettings => {
        if (employeeSettings && employeeSettings.value) {
          return JSON.parse(employeeSettings.value) as EmployeeCardFilterSetting[];
        }
        return [];
      }),
      tap(settings => {
        this._employeeSettingSubject.next(settings);
        this.cachedCardFilterSettings = settings;
      })
    );
  }

  /**
   * Gets refreshed employee card filter setting for a specific object type
   * @param objectType - The object type to filter by
   * @returns Observable of EmployeeCardFilterSetting for the specified object type
   */
  getRefreshedEmployeeCardFilterSetting(objectType: ObjectTypes): Observable<EmployeeCardFilterSetting> {
    return this.employeeSettingObservable.pipe(
      map((settings) => {
        this.cachedCardFilterSettings = settings;
        return settings.find((r) => r.objectType === objectType);
      })
    );
  }

  /**
   * Gets all refreshed employee card filter settings and notifies subscribers
   * @returns Observable of all EmployeeCardFilterSetting array
   */
  getRefreshedEmployeeCardFilterSettingAll(): Observable<EmployeeCardFilterSetting[]> {
    return this.employeeSettingObservable.pipe(
      tap(settings => {
        this._settingsChanged.next(settings);
      })
    );
  }

  /**
   * Gets the employee card filter setting for a specific object type
   * @param objectType - The object type to get settings for
   * @returns The EmployeeCardFilterSetting for the specified object type, or null if not found
   */
  public getEmployeeCardFilterSetting(objectType: ObjectTypes): EmployeeCardFilterSetting {
    if (!this.cachedCardFilterSettings || this.cachedCardFilterSettings.length === 0) {
      return null;
    }

    return this.cachedCardFilterSettings.find(s => s.objectType === objectType) || null;
  }

  /**
   * Gets an observable of the employee card filter setting for a specific object type
   * @param objectType - The object type to get settings for
   * @returns Observable of the EmployeeCardFilterSetting for the specified object type
   */
  public getEmployeeCardFilterSetting$(objectType: ObjectTypes): Observable<EmployeeCardFilterSetting> {
    return this.employeeSetting$.pipe(
      map(settings => settings?.find(s => s.objectType === objectType) || null)
    );
  }

  /**
   * Compare Function for strings that compares string by length first and if they have same length , compare them by characters.
   * (First - Second) is the strategy , so swap the params when you pass them to achieve DESCENDING or ASCENDING
   * @param first swap with second(param) to change the order (descending / ascending)
   * @param second swap with second(param) to change the order (descending / ascending)
   */
  public stringsCompareFn(first: string, second: string) {
    return first.localeCompare(second) || first.length - second.length;
  }

  /**
   * Compare function for dates that compares two dates by their timestamp
   * @param first - First date to compare (string or Date)
   * @param second - Second date to compare (string or Date)
   * @returns Negative if first is earlier, positive if first is later, 0 if equal
   */
  public datesCompareFn(first: string | Date, second: string | Date) {
    return new Date(first).getTime() - new Date(second).getTime();
  }

  /**
   * Sorts filters based on FilterType - either by enum value or by text
   * @param filters - Array of FilterViewModel to sort
   * @param filterType - The type of filter to determine sorting strategy
   * @returns Sorted array of FilterViewModel
   */
  sortFilters(filters: FilterViewModel[], filterType: FilterTypes): FilterViewModel[] {
    let coppiedFilters = filters.slice();

    if (this.filtersForSortBasedOnEnum.some((ft) => ft === filterType)) {
      coppiedFilters = coppiedFilters.sort((a, b) => +a.filterValue - +b.filterValue);
    } else {
      coppiedFilters = coppiedFilters.sort((a, b) => a.filterText && a.filterText?.localeCompare(b.filterText));
    }

    return coppiedFilters;
  }

  /**
   * Sorts a list of entities by their filters based on the specified filter type
   * Used in lists to sort items by various filter criteria
   * @param arr - Array of entities to sort (must extend ModifiableEntityViewModel)
   * @param filterType - The type of filter to sort by
   * @param ascending - Whether to sort in ascending (true) or descending (false) order
   * @param dateProperty - Optional date property for date-based sorting
   * @returns Sorted array of entities
   */
  sortListByFilters<T extends ModifiableEntityViewModel>(
    arr: T[],
    filterType: FilterTypes,
    ascending: boolean,
    dateProperty?: FilterDateOptions
  ): T[] {
    const allowedFiltersByFilterType = this.allowedFiltersService.getCachedAllowedFiltersByType(filterType);

    // We are using this one in cases when the filters dont have FilterText, so this means that we need to extract them from the allowedFilters
    const filterTypesToGetFromAllowedFilters = [
      FilterTypes.Owner,
      FilterTypes.Department,
    ];
    const isUsingAllowedFiltersToCompare = filterTypesToGetFromAllowedFilters.some((ft) => ft === filterType);

    const isBasedOnFilterValue = this.filtersToSortByFilterValue.some((ft) => ft === filterType);
    const isDateSorting = filterType === FilterTypes.Date && dateProperty;

    const compareMappedAllowedFilters = (a: FilterViewModel, b: FilterViewModel) => {
      const aFoundedFilter = allowedFiltersByFilterType.find((f) => f.filterValue == a.filterValue);
      const bFoundedFilter = allowedFiltersByFilterType.find((f) => f.filterValue == b.filterValue);
      if (aFoundedFilter && bFoundedFilter) {
        return ascending
          ? aFoundedFilter.filterText.localeCompare(bFoundedFilter.filterText)
          : bFoundedFilter.filterText.localeCompare(aFoundedFilter.filterText);
      } else if (!aFoundedFilter) {
        return ascending ? 1 : -1;
      } else if (!bFoundedFilter) {
        return ascending ? -1 : 1;
      }
    };

    return [...arr].sort((a, b) => {
      let result = 0;

      let aMatchingFilters: FilterViewModel[];
      let bMatchingFilters: FilterViewModel[];

      if (isDateSorting) {
        aMatchingFilters = a.filters.filter((f: FilterViewModel) => f.filterType === filterType && f.dateProperty === dateProperty);
        bMatchingFilters = b.filters.filter((f: FilterViewModel) => f.filterType === filterType && f.dateProperty === dateProperty);
      } else {
        const aListItemFiltersByFT: FilterViewModel[] = a.filters.filter((f) => f.filterType === filterType);
        const bListItemFiltersByFT: FilterViewModel[] = b.filters.filter((f) => f.filterType === filterType);
        if (isUsingAllowedFiltersToCompare) {
          aMatchingFilters = aListItemFiltersByFT?.sort((c, d) => compareMappedAllowedFilters(c, d));
          bMatchingFilters = bListItemFiltersByFT?.sort((c, d) => compareMappedAllowedFilters(c, d));
        } else {
          aMatchingFilters = aListItemFiltersByFT?.sort((c, d) =>
            ascending ? c.filterText.localeCompare(d.filterText) : d.filterText.localeCompare(c.filterText)
          );
          bMatchingFilters = bListItemFiltersByFT?.sort((c, d) =>
            ascending ? c.filterText.localeCompare(d.filterText) : d.filterText.localeCompare(c.filterText)
          );
        }
      }

      if (aMatchingFilters?.length && bMatchingFilters?.length) {
        if (isBasedOnFilterValue) {
          if (isDateSorting) {
            result = ascending
              ? this.datesCompareFn(aMatchingFilters[0].filterValue, bMatchingFilters[0].filterValue)
              : this.datesCompareFn(bMatchingFilters[0].filterValue, aMatchingFilters[0].filterValue);
          } else if (this.MappedEnumBasedFilterTypes[filterType]) {
            const mappedFirstFilterValue = this.MappedEnumBasedFilterTypes[filterType][aMatchingFilters[0].filterValue];
            const mappedSecondFilterValue = this.MappedEnumBasedFilterTypes[filterType][bMatchingFilters[0].filterValue];
            result = ascending
              ? this.stringsCompareFn(mappedFirstFilterValue, mappedSecondFilterValue)
              : this.stringsCompareFn(mappedSecondFilterValue, mappedFirstFilterValue);
          } else {
            result = ascending
              ? this.stringsCompareFn(aMatchingFilters[0].filterValue, bMatchingFilters[0].filterValue)
              : this.stringsCompareFn(bMatchingFilters[0].filterValue, aMatchingFilters[0].filterValue);
          }
        } else {
          result = ascending
            ? this.stringsCompareFn(aMatchingFilters[0].filterText, bMatchingFilters[0].filterText)
            : this.stringsCompareFn(bMatchingFilters[0].filterText, aMatchingFilters[0].filterText);
        }
      } else if (aMatchingFilters?.length) {
        result = ascending ? -1 : 1;
      } else if (bMatchingFilters?.length) {
        result = ascending ? 1 : -1;
      }

      return result;
    });
  }

  /**
   * Manually triggers updates to notify subscribers of settings changes
   * Used after settings are saved to ensure all components are updated
   * @param settings - The updated EmployeeCardFilterSetting array
   */
  public notifySettingsChanged(settings: EmployeeCardFilterSetting[]): void {
    // Update both subjects
    this._employeeSettingSubject.next(settings);
    this._settingsChanged.next(settings);
    this.cachedCardFilterSettings = settings;
  }
}
